import { useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useContent } from "@/contexts/ContentContext";
import { ICON_OPTIONS } from "@/lib/icons";
import ImageUploader from "@/components/admin/ImageUploader";
import BannerEditor from "@/components/admin/BannerEditor";

export const FeaturedEditor = () => {
  const { content, update } = useContent();
  const [draft, setDraft] = useState(content.featured);
  const [busy, setBusy] = useState(false);

  const onField = (k) => (e) => setDraft({ ...draft, [k]: e.target.value });

  const updatePillar = (i, k, v) => {
    const pillars = [...(draft.pillars || [])];
    pillars[i] = { ...pillars[i], [k]: v };
    setDraft({ ...draft, pillars });
  };
  const addPillar = () => setDraft({ ...draft, pillars: [...(draft.pillars || []), { icon: "Sparkles", title: "New pillar", desc: "Short description." }] });
  const removePillar = (i) => {
    const pillars = (draft.pillars || []).filter((_, idx) => idx !== i);
    setDraft({ ...draft, pillars });
  };

  const updateCheck = (i, v) => {
    const checks = [...(draft.checks || [])];
    checks[i] = v;
    setDraft({ ...draft, checks });
  };
  const addCheck = () => setDraft({ ...draft, checks: [...(draft.checks || []), "New highlight"] });
  const removeCheck = (i) => setDraft({ ...draft, checks: (draft.checks || []).filter((_, idx) => idx !== i) });

  const save = async () => {
    setBusy(true);
    try {
      await update("featured", draft);
      toast.success("Featured app updated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="featured-editor">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Eyebrow</Label>
          <Input data-testid="featured-editor-eyebrow" value={draft.eyebrow || ""} onChange={onField("eyebrow")} />
        </div>
        <div className="space-y-1.5">
          <Label>Status badge</Label>
          <Input data-testid="featured-editor-badge" value={draft.badge_label || ""} onChange={onField("badge_label")} />
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>Title (line 1)</Label>
          <Input data-testid="featured-editor-title1" value={draft.title_line1 || ""} onChange={onField("title_line1")} />
        </div>
        <div className="space-y-1.5">
          <Label>Title (line 2, accent)</Label>
          <Input data-testid="featured-editor-title2" value={draft.title_line2 || ""} onChange={onField("title_line2")} />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Description</Label>
        <Textarea rows={3} data-testid="featured-editor-desc" value={draft.description || ""} onChange={onField("description")} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <Label>App Store URL</Label>
          <Input data-testid="featured-editor-appstore" value={draft.app_store_url || ""} onChange={onField("app_store_url")} placeholder="https://apps.apple.com/..." />
        </div>
        <div className="space-y-1.5">
          <Label>Learn-more URL</Label>
          <Input data-testid="featured-editor-learnmore" value={draft.learn_more_url || ""} onChange={onField("learn_more_url")} placeholder="https://..." />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 rounded-2xl border border-[hsl(var(--border))] bg-white p-4">
        <div className="lg:col-span-4">
          <ImageUploader
            value={draft.phone_screen_image_id}
            onChange={(id) => setDraft({ ...draft, phone_screen_image_id: id })}
            label="Featured phone screen image"
            testId="featured-editor-phone-image"
            aspect="9/19.5"
          />
        </div>
        <div className="lg:col-span-8 flex items-center">
          <p className="text-sm text-[hsl(var(--muted-foreground))]">
            Optional. Upload a portrait app screenshot (recommended 9:19.5 ratio) to replace the built-in Breed Detective preview inside the Featured phone. Clear it to bring the default art back.
          </p>
        </div>
      </div>

      <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--accent))]/40 p-4 sm:p-5">
        <BannerEditor
          banners={draft.floating_banners}
          onChange={(next) => setDraft({ ...draft, floating_banners: next })}
          testIdPrefix="featured-banner"
          helperText="Optional floating tiles around the Featured phone (hidden on mobile). Tip: “Top · Right” / “Bottom · Right” work best because the phone sits on the left."
        />
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-base">Feature pillars</Label>
          <Button type="button" variant="outline" size="sm" onClick={addPillar} data-testid="featured-editor-pillar-add" className="rounded-lg">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add pillar
          </Button>
        </div>
        <div className="mt-3 space-y-3">
          {(draft.pillars || []).map((p, i) => (
            <div key={i} data-testid={`featured-editor-pillar-${i}`} className="rounded-xl border border-[hsl(var(--border))] bg-white p-4 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-12 gap-3">
                <div className="sm:col-span-3 space-y-1">
                  <Label className="text-xs">Icon</Label>
                  <Select value={p.icon || "Sparkles"} onValueChange={(v) => updatePillar(i, "icon", v)}>
                    <SelectTrigger data-testid={`featured-editor-pillar-${i}-icon`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ICON_OPTIONS.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-4 space-y-1">
                  <Label className="text-xs">Title</Label>
                  <Input data-testid={`featured-editor-pillar-${i}-title`} value={p.title || ""} onChange={(e) => updatePillar(i, "title", e.target.value)} />
                </div>
                <div className="sm:col-span-4 space-y-1">
                  <Label className="text-xs">Description</Label>
                  <Input data-testid={`featured-editor-pillar-${i}-desc`} value={p.desc || ""} onChange={(e) => updatePillar(i, "desc", e.target.value)} />
                </div>
                <div className="sm:col-span-1 flex items-end justify-end">
                  <Button type="button" variant="ghost" size="icon" onClick={() => removePillar(i)} data-testid={`featured-editor-pillar-${i}-remove`} className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14]">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <Label className="text-base">Highlights (check bullets)</Label>
          <Button type="button" variant="outline" size="sm" onClick={addCheck} data-testid="featured-editor-check-add" className="rounded-lg">
            <Plus className="h-3.5 w-3.5 mr-1" /> Add highlight
          </Button>
        </div>
        <div className="mt-3 space-y-2">
          {(draft.checks || []).map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input data-testid={`featured-editor-check-${i}`} value={c} onChange={(e) => updateCheck(i, e.target.value)} />
              <Button type="button" variant="ghost" size="icon" onClick={() => removeCheck(i)} className="text-[hsl(var(--muted-foreground))] hover:text-[#D94A14]">
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={busy} data-testid="featured-editor-save" className="h-10 rounded-xl bg-[#F35A1F] text-white hover:bg-[#D94A14]">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save Featured
        </Button>
      </div>
    </div>
  );
};

export default FeaturedEditor;
